<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <div class="card">
      <div class="card-body">
        <div class="row">
          <div class="col-sm-6">
            <h4 qid="n-view-page-title" class="mb-3">
              {{ network.name }}
            </h4>
            <div class="mb-2">
              <span v-if="getGuide('network')">
                <GuideButton :guide="getGuide('network')"></GuideButton>
              </span>
            </div>
          </div>

          <div class="col-md-6 text-right" qid="view-n-modified">
            <div class="mb-4">
                <span class="h6 ml-3">
                <span v-if="isGranted([permissions.NETWORK_EDIT, permissions.NETWORK_EDIT_OWN], network.created_by) && (!getClient.parent || (getClient.parent && network.partition))"
                      class="btn btn-edit"
                      qid="edit-n-button"
                      @click="showEditNModal(network)"
                >
                  <font-awesome-icon icon="pen"/>
                </span>
                <span v-if="isGranted([permissions.NETWORK_DELETE, permissions.NETWORK_DELETE_OWN], network.created_by) && (!getClient.parent || (getClient.parent && network.partition))"
                      class="btn btn-delete"
                      qid="delete-n-button"
                      @click="showConfirmDeleteModal(network)"
                >
                  <font-awesome-icon icon="trash-alt"/>
                </span>
              </span>
            </div>
            <div>
              <span>{{ $t('table.last_updated') }}</span>
              <span class="ml-2 text-muted small" qid="view-n-modified-date">
              {{ network.updated_at | formatDate }}
            </span>
              <Avatar v-if="network.updated_by" :object="network.updated_by" size="xs" class="ml-3 mr-2"></Avatar>
              <span v-if="network.updated_by" qid="view-n-modified-by">
                {{ network.updated_by.name }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-tabs class="mt-3">

      <!-- First tab -->
      <b-tab :title="$t('maintenance.network.tab.basic_info')" active>
        <div class="card">
          <div class="card-body">
            <div class="content-view-body">
              <br>
              <div class="mt-3">
                <p class="text-dark">
                  {{ $t('maintenance.network.create.exposed_to_open_networks_label') }}
                </p>
                <hr>
                <p>
                  <span v-if="network.exposed_to_open_networks == 1" qid="n-exposed-yes">
                    {{ $t('system.yes') }}
                  </span>
                  <span v-if="network.exposed_to_open_networks == 0" qid="n-exposed-no">
                    {{ $t('system.no') }}
                  </span>
                </p>
                <br>
              </div>
            </div>
          </div>
        </div>
      </b-tab>

      <!-- Second tab -->
      <b-tab :title="$t('maintenance.network.tab.linked_systems')" class="mt-3">
        <SystemsTable :object="network"></SystemsTable>
      </b-tab>
    </b-tabs>


    <!-- Edit information obligation modal template -->
    <b-modal ref="edit-network" hide-footer>
      <div class="d-block text-center">
        <h4 qid="update-n-title">
          {{ $t('maintenance.network.update.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="edit-n-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="editNFormSubmit" qid="create-n-form">
        <div class="form-container">
          <b-form-group>
            <label qid="edit-n-name-label">
              {{ $t('maintenance.network.create.name_label') }}
            </label>
            <b-form-input
              qid="edit-n-form-name"
              type="text"
              v-model="nForm.name"
              :maxLength="255"
              required
              :placeholder="$t('maintenance.network.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="edit-n-exposed-label">
              {{ $t('maintenance.network.create.exposed_to_open_networks_label') }}
            </label>
            <b-form-radio-group
              v-model="nForm.exposed_to_open_networks"
              :options="radioOptions"
              qid="edit-n-form-exposed"
            ></b-form-radio-group>
          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideEditNModal"
                    qid="edit-n-cancel-button"
          >
            {{ $t('maintenance.network.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    variant="success"
                    :disabled="buttonDisabled"
                    qid="edit-n-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('maintenance.network.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>


    <!-- Delete information obligation confirm modal template -->
    <b-modal ref="delete-network" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-n-title">
          {{ $t('maintenance.network.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('maintenance.network.delete.description') }} {{ itemToDelete.name }}
      </h5>
      <h5 class="text-center">
        {{ $t('maintenance.network.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideConfirmDeleteModal"
                  qid="delete-n-cancel-button"
        >
          {{ $t('maintenance.network.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteN(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-n-submit-button"
                  v-if="isGranted([permissions.NETWORK_DELETE, permissions.NETWORK_DELETE_OWN], itemToDelete.created_by) && (!getClient.parent || (getClient.parent && itemToDelete.partition))"
        >
          {{ $t('maintenance.network.delete.submit') }}
        </b-button>
      </div>
    </b-modal>

  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js'
import {config} from '@/config.js'
import Avatar from '@/components/Avatar.vue';
import SystemsTable from '@/views/Network/SystemsTable.vue';
import GuideButton from '@/components/guide/GuideButton.vue';


export default {
  name: 'NetworkView',
  data: function() {
    return {
      breadcrumbItems: [
        {
          text: this.$t('dashboard.title'),
          to: '/'
        },
        {
          text: this.$t('maintenance.title'),
          to: '/maintenance'
        },
        {
          text: this.$t('maintenance.network.title'),
          to: '/maintenance/network'
        }
      ],
      network: {},
      itemToDelete: {},
      nForm: {
        name: '',
        exposed_to_open_networks: '',
        client:'',
      },
      error: '',
      radioOptions: [
        {text: this.$t('system.yes'), value: 1},
        {text: this.$t('system.no'), value: 0}
      ],
      buttonDisabled: false,
      myGuides: [],
    }
  },
  computed: {
    rows() {
      // Use computed value for rows because of dynamic update
      return this.totalRows
    },
    getClient() {
      return this.$store.state.client
    }
  },
  watch: {
    getClient: function (newValue, oldValue) {
      if (newValue && oldValue && newValue.slug !== oldValue.slug) {
        this.goToList()
      }
    }
  },
  components: {
    Avatar,
    SystemsTable,
    GuideButton
  },
  methods: {
    goToList() {
      this.$router.push('/maintenance/network')
    },
    getGuide(code) {
      let existingGuide = _.find(this.myGuides, function(object) {
        return object.scope == code && object.type == 'Maintenance'
      });
      return existingGuide
    },
    loadGuides(code) {
      let self = this;
      let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

      piincHttp.get('guides', { params:
        {
          per_page: 1,
          language: 'en',
          type: 'Maintenance',
          scope: code,
          client: this.getClient.slug
        }
      }).then(function(response) {
        if (response.data.items.length) {
          self.myGuides.push(response.data.items[0])
        }
      }, function() {});
    },
    loadContent() {
      let self = this;
      piincHttp.get('networks/' + this.$route.params.slug, { params:
        {
          client: this.getClient.slug
        }
      }).then(function(response) {
        self.network = response.data;
        // Push Information obligation name in breadcrumb array
        self.breadcrumbItems.push({text: self.network.name, active: true});
      }, function() {});
    },
    getUserAvatar(user){
      if (user && user.avatar) {
        return user.avatar;
      } else {
        return require('@/assets/avatar.png');
      }
    },
    showConfirmDeleteModal(object) {
      this.itemToDelete = object
      this.$refs['delete-network'].show()
    },
    hideConfirmDeleteModal() {
      this.itemToDelete = {}
      this.$refs['delete-network'].hide()
    },
    confirmDeleteN(object) {
      let self = this;
      piincHttp.delete('networks/' + object.slug).then(function() {
        self.hideConfirmDeleteModal();
        self.goToList();
        // Trigger toastr message after modal close
        self.$toastr('success', self.$t('maintenance.network.delete.success'))
      }, function() {});
    },
    showEditNModal(object) {
      this.nForm.slug = object.slug;
      this.nForm.name = object.name;
      this.nForm.exposed_to_open_networks = object.exposed_to_open_networks;
      this.$refs['edit-network'].show()
    },
    hideEditNModal() {
      this.$refs['edit-network'].hide()
    },
    editNFormSubmit(evt) {
      this.buttonDisabled = true;
      var moment = require('moment');
      // Keep the reference in a variable
      // to use it inside function called when promise returned
      let self = this;
      evt.preventDefault();
      this.nForm.client = this.getClient.slug;

      piincHttp.put('networks/' + this.nForm.slug, this.nForm).then(function(response) {
        self.hideEditNModal();
        self.buttonDisabled = false;
        // If slug is not changed, just reload content
        if (response.data.slug === self.network.slug) {
          // Remove last item in breadcrumbs, because it will be added again on load content
          self.breadcrumbItems.pop();
          self.loadContent();
        } else {
          // If slug changed, reload state with new param
          self.$router.push({ name: 'maintenance.network.view', params: { slug: response.data.slug }})
        }
        self.$toastr('success', self.$t('maintenance.network.update.success'))
      }, function(error) {
        if (error.status !== 200) {
          self.buttonDisabled = false;
          self.error = error.data.message;
        }
      })
    }
  },
  created () {
    this.loadContent()
    this.loadGuides('network');
  }
}
</script>
